import React from 'react';
import { Flex } from 'app/components/common/Flex';
import { Avatar, Badge, Input, notification, Select, Tooltip } from 'antd';
import {
    ChatData,
    ChatMessage,
    ChatPresence,
    ChatPresence_Status,
    FastPollingChatData,
    ResponseId,
    UpdateType,
} from 'app/model/CORE';
import { ServiceFactory } from 'app/service/ServiceFactory';
import Utils from 'app/common/util/Utils';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import TimeUtils from 'app/common/util/TimeUtils';
import KeyUtils from 'app/common/util/KeyUtils';
import { ProvidesKey } from 'app/model/client/CORE';
import AppContext from 'app/common/context/AppContext';
import memoizeOne from 'memoize-one';
import ConditionalRender from 'app/common/components/ConditionalRender';
import { setNewChatMessages } from 'app/redux/features/dashboard/dashboard-slice';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'app/redux/store';

const CHAT_PRESENCE_KEY_PROVIDER = new ProvidesKey<ChatPresence>(obj => obj.userId);

const mapStateToProps = (state: RootState) => {
    return {};
};

const mapDispatchToProps = {
    setNewChatMessages,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface ChatPanelState {
    locallyAddedMessageIdx?: number;
    message?: string;
    toUserId?: string;
    presenceEditorVisible?: boolean;
    userPausedPolling?: boolean;
    knownOtherUsers?: string[];
    chatData?: ChatData;
}

interface ChatPanelProps extends PropsFromRedux {
    /** The userId of the user accessing the application */
    userId: string;

    /**
     * Polling interval in milliseconds.
     * Values <= 0 will imply no polling required, and the user will call manually
     *  processFastPollingData
     */
    pollingIntervalInMs: number;

    /** Allows the application to pause all polling (e.g. prior to shutdown) */
    pausePolling: boolean;

    /** A number of consecutive polling failures above this threshold will lead to 'onCommunicationFailure' being called */
    maxNumPollingFailures: number;
    onChatFailure: () => void;

    /** callback to inform the application of new chat messages */
    onNewChat: () => void;
}

export class ChatPanel extends React.PureComponent<ChatPanelProps, ChatPanelState> {
    private timer: number;
    private fastPollingCallOngoing: boolean;
    private numCommFailures: number = 0;
    private prevChatResponseId?: ResponseId;
    protected messagesRef = React.createRef<HTMLDivElement>();
    private static ALL_USER = 'All Users';

    private needToScrollDown: boolean = true;

    constructor(props) {
        super(props);
        this.state = {
            locallyAddedMessageIdx: -1,
            presenceEditorVisible: false,
            userPausedPolling: false,
            knownOtherUsers: [ChatPanel.ALL_USER],
            toUserId: ChatPanel.ALL_USER,
        };
    }

    componentDidMount(): void {
        if (this.props.pollingIntervalInMs > 0) {
            this.timer = window.setInterval(() => this.cyclicPolling(), this.props.pollingIntervalInMs);
            this.cyclicPolling(); // fire immediately as well
        }
    }

    componentWillUnmount(): void {
        if (this.timer) {
            console.log('Stopping ChatPanel polling');
            window.clearInterval(this.timer);
        }
    }

    /**
     * Scroll down automatically to the last received message.
     * TODO : Does not work immediaetly when open panel.. haven't quite fully understood
     * @param prevProps
     * @param prevState
     * @param snapshot
     */
    componentDidUpdate(prevProps: Readonly<ChatPanelProps>, prevState: Readonly<ChatPanelState>, snapshot?: any): void {
        if (this.needToScrollDown) {
            const divEl = this.messagesRef.current;
            if (divEl && divEl.clientHeight > 0) {
                console.log('Scroll top' + divEl.scrollTop);
                console.log('Scroll height' + divEl.scrollHeight);
                console.log('Client height' + divEl.clientHeight);
                divEl.scrollTo({ top: divEl.scrollHeight - divEl.clientHeight });
                this.needToScrollDown = false;
            }
        }
    }

    render() {
        const messagesPerUser = this.getMessagesPerUser(this.state.chatData?.messages);
        const users = Utils.isExists(messagesPerUser?.keys()) ? Array.from(messagesPerUser.keys()) : [];
        const unreadMessagesPerUser = new Map<string, number>();
        messagesPerUser?.forEach((value, key) => {
            const unread = value.filter(msg => msg['read'] === false)?.length;
            unreadMessagesPerUser.set(key, unread);
        });

        return (
            <Flex direction="vertical" className="coordination-chat overflow">
                <Flex size="2em" className="header">
                    CHAT
                </Flex>
                <Flex size="4em" style={{ padding: '1em' }}>
                    {this.renderUsers(
                        this.getUsers(this.state.knownOtherUsers, users),
                        this.state.chatData?.presences,
                        unreadMessagesPerUser,
                    )}
                </Flex>
                <Flex
                    size="10em"
                    direction="vertical"
                    className={`footer ${this.state.userPausedPolling ? 'disabled' : ''}`}>
                    <Input
                        style={{ flexBasis: '5em' }}
                        value={this.state.message}
                        placeholder="Enter your message..."
                        onChange={event => this.setState({ message: event.target.value })}
                        onKeyDown={this.doHandleMessageKeyDown}
                    />
                    <Flex direction="horizontal">
                        <Flex className="centered" size="2em">
                            TO:
                        </Flex>
                        <Flex className="centered">
                            <Select
                                style={{ width: '100%' }}
                                value={this.state.toUserId}
                                onChange={this.doChangeSendTo}>
                                {this.getUsers(this.state.knownOtherUsers, users).map(userId => (
                                    <Select.Option key={userId} value={userId}>
                                        {userId}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Flex>
                    </Flex>
                </Flex>

                {this.renderChat(messagesPerUser?.get(this.state.toUserId))}
            </Flex>
        );
    }

    getUsers = memoizeOne((knownOtherUsers: string[], existingUsersFromChats: string[]) => {
        const result = [...knownOtherUsers];
        existingUsersFromChats?.forEach(user => {
            if (!Utils.contains(result, user)) {
                result.push(user);
            }
        });
        return result;
    });

    renderChat(messages: Array<ChatMessage>) {
        return (
            <div className="chat overflow" ref={this.messagesRef}>
                <ConditionalRender condition={Utils.isNotExists(messages?.length)}>
                    <div style={{ textAlign: 'center', padding: '0.5em' }}>No messages</div>
                </ConditionalRender>
                {messages?.map((chatMessage, idx) => {
                    const myMessageClassname = chatMessage.fromId === AppContext.configData.claimedUserId ? 'mine' : '';
                    const chatMessageClassname = `chat-message ${myMessageClassname}`;
                    const messageClassname = `message`;
                    const fromId = <>{`${chatMessage.fromId}`}</>;
                    return (
                        <div key={idx} className={chatMessageClassname}>
                            <Flex className="title">
                                <span className="timestamp">{TimeUtils.formatHHMM(chatMessage.timestamp)}</span>
                                <span className="from">{fromId}</span>
                            </Flex>
                            <div className={messageClassname}>{chatMessage.message}</div>
                        </div>
                    );
                })}
            </div>
        );
    }

    renderUsers(users: Array<string>, presences: Array<ChatPresence>, unreadMessagesPerUser: Map<string, number>) {
        const presencesPerUserId = presences?.reduce((map, cp) => {
            if (!map.has(cp.userId)) {
                map.set(cp.userId, cp);
            }
            return map;
        }, new Map<string, ChatPresence>());
        return (
            <ConditionalRender condition={users?.length > 0}>
                <Avatar.Group
                    maxCount={14}
                    size="large"
                    maxStyle={{ color: '#313131', backgroundColor: '#60B1CC', cursor: 'pointer' }}
                    maxPopoverTrigger="click"
                    style={{ gap: '0.5em' }}>
                    {users?.map(userId => {
                        let presence = presencesPerUserId?.get(userId);
                        if (Utils.isNotExists(presence)) {
                            presence = {
                                userId: userId,
                                status: ChatPresence_Status.OFFLINE,
                            };
                        }
                        const isCurrentSelection = presence?.userId === this.state.toUserId;
                        return (
                            <Tooltip key={`tooltip-${presence.userId}`} title={presence.userId} placement="top">
                                <Badge
                                    key={`badge-${presence.userId}`}
                                    count={unreadMessagesPerUser?.get(userId)}
                                    style={{ zIndex: isCurrentSelection ? 1000 : 10 }}>
                                    <Avatar
                                        key={`avatar-${presence.userId}`}
                                        onClick={() => this.handleAvatarClick(presence)}
                                        style={{
                                            color: isCurrentSelection ? '#313131' : 'white',
                                            borderColor: this.getAvatarStatusColor(presence),
                                            borderWidth: '3px',
                                            backgroundColor: isCurrentSelection ? undefined : '#313131',
                                        }}>
                                        {presence.userId
                                            .substring(0, Math.min(presence.userId.length, 4))
                                            .toUpperCase()}
                                    </Avatar>
                                </Badge>
                            </Tooltip>
                        );
                    })}
                </Avatar.Group>
            </ConditionalRender>
        );
    }

    getAvatarStatusColor(presence: ChatPresence) {
        //all users always online
        const presenceState = presence.userId === ChatPanel.ALL_USER ? ChatPresence_Status.ONLINE : presence.status;
        switch (presenceState) {
            case ChatPresence_Status.OFFLINE:
                return '#696969';
            case ChatPresence_Status.ONLINE:
                return '#60B1CC';
            case ChatPresence_Status.ABSENT:
                return '#696969';
            case ChatPresence_Status.BUSY:
                return '#FF1919';
            case ChatPresence_Status.AVAILABLE:
                return '#60B1CC';
        }
    }

    handleAvatarClick = (presence: ChatPresence) => {
        this.doChangeSendTo(presence.userId);
    };

    getMessagesPerUser = memoizeOne((messages: Array<ChatMessage>) => {
        return messages
            ?.filter(
                msg =>
                    Utils.isNotExists(msg.toId) ||
                    msg.toId === AppContext.configData.claimedUserId ||
                    msg.fromId === AppContext.configData.claimedUserId,
            )
            .reduce((map, msg: ChatMessage) => {
                //all users tab
                if (Utils.isNotExists(msg.toId)) {
                    if (!map.has(ChatPanel.ALL_USER)) {
                        map.set(ChatPanel.ALL_USER, []);
                    }
                    map.get(ChatPanel.ALL_USER).push(msg);
                } else {
                    const userName = msg.toId === AppContext.configData.claimedUserId ? msg.fromId : msg.toId;
                    if (!map.has(userName)) {
                        map.set(userName, []);
                    }
                    map.get(userName).push(msg);
                }
                return map;
            }, new Map<string, Array<ChatMessage>>());
    });

    // HANDLERS

    doHandleMessageKeyDown = ev => {
        if (ev.keyCode === 13 && !ev.shiftKey) {
            this.doSendMessage();
            ev.stopPropagation();
            ev.preventDefault();
        }
    };

    doPresenceEditorVisible = (visible: boolean) => {
        this.setState({ presenceEditorVisible: visible });
    };

    doChangeUserPausedPolling = (event: CheckboxChangeEvent) => {
        this.setState({ userPausedPolling: event.target.checked });
    };
    doChangeSendTo = (value: string) => {
        this.setState({
            toUserId: value,
        });
        this.markAsRead(value);
    };

    markAsRead(toId: string) {
        this.setState(
            prevState => {
                let requiresUpdate = false;
                const newMessages = prevState.chatData?.messages?.map(msg => {
                    if (
                        (msg.toId === AppContext.configData.claimedUserId && msg.fromId === toId) ||
                        (Utils.isNotExists(msg.toId) && toId === ChatPanel.ALL_USER)
                    ) {
                        requiresUpdate = true;
                        return Object.assign({}, msg, { read: true });
                    } else {
                        return msg;
                    }
                });
                if (requiresUpdate) {
                    const newChatData: ChatData = Object.assign({}, prevState.chatData, { messages: newMessages });
                    return {
                        chatData: newChatData,
                    };
                }
            },
            () => this.refreshGlobalReadIndicator(),
        );
    }

    refreshGlobalReadIndicator() {
        this.props.setNewChatMessages(
            this.state.chatData.messages.filter(
                msg =>
                    msg['read'] === false &&
                    (Utils.isNotExists(msg.toId) || msg.toId === AppContext.configData.claimedUserId),
            )?.length || 0,
        );
    }

    doSendMessage = () => {
        ServiceFactory.technicalService()
            .postChatMessage({
                message: this.state.message,
                fromId: this.props.userId,
                toId: this.state.toUserId === ChatPanel.ALL_USER ? undefined : this.state.toUserId,
                timestamp: TimeUtils.NOW(), // will be replaced by server timestamp
            })
            .then(reply => {
                if (reply.error) {
                    notification.error({ message: 'Error', description: reply.error.message, duration: 0 });
                } else {
                    // manually add the sent message to the chatData, so it will be immediately displayed, rather than waiting for the next polling call.
                    // when the next polling call returns, we need to ensure this message is not added a second time to the chatData.
                    this.needToScrollDown = true;
                    this.setState(state => ({
                        message: '',
                        locallyAddedMessageIdx:
                            state.locallyAddedMessageIdx === -1
                                ? Utils.size(state.chatData?.messages)
                                : state.locallyAddedMessageIdx,
                        chatData: {
                            messages: [...state.chatData?.messages, reply.data],
                            presences: state.chatData?.presences,
                        },
                    }));
                }
            })
            .catch(error => notification.error({ message: 'Error', description: error, duration: 0 }));
    };

    private cyclicPolling() {
        if (!this.props.pausePolling && !this.state.userPausedPolling && !this.fastPollingCallOngoing) {
            ServiceFactory.technicalService()
                .getChatDataSince(this.props.userId, this.prevChatResponseId)
                .then(reply => {
                    this.numCommFailures = 0;
                    if (reply.error) {
                        notification.error({ message: 'Error', description: reply.error.message, duration: 0 }); // should never happen .. so display a message!!
                    } else {
                        this.prevChatResponseId = reply.data.responseId;
                        this.processFastPollingData(reply.data);
                    }
                })
                .catch(error => {
                    this.numCommFailures++;
                    if (this.numCommFailures > this.props.maxNumPollingFailures) {
                        // this.onChatFailure();
                    }
                })
                .finally(() => {
                    this.fastPollingCallOngoing = false;
                });
            this.fastPollingCallOngoing = true;
        }
    }

    /**
     * Process the fast polling data
     * In most cases, this will be an incremental update, where we add 'delta' messages/presence to the
     * already cached values.
     * Care must be taken if the user has recently posted a message to not count it twice
     * (since it has already been added in the sendMessage method)
     * @param data
     */
    processFastPollingData(fastPolling: FastPollingChatData) {
        this.setState(state => {
            const newState: ChatPanelState = {};
            if (state.locallyAddedMessageIdx !== -1) {
                newState.locallyAddedMessageIdx = -1;
            }
            const newOtherPresences = fastPolling.chatData?.presences
                ?.map(cp => cp.userId)
                .filter(id => id !== this.props.userId);
            switch (fastPolling.updateType) {
                case UpdateType.FULL_UPDATE:
                case UpdateType.RESET_AND_FULL_UPDATE:
                    newState.chatData = fastPolling.chatData;
                    newState.knownOtherUsers = [ChatPanel.ALL_USER, ...newOtherPresences];
                    this.needToScrollDown = true;
                    break;
                case UpdateType.PARTIAL_UPDATE:
                    let newMessages: Array<ChatMessage>;
                    if (state.locallyAddedMessageIdx === -1) {
                        newMessages = fastPolling.chatData?.messages;
                    } else {
                        const locallyAdded = state.chatData?.messages?.slice(state.locallyAddedMessageIdx);
                        newMessages = fastPolling.chatData?.messages?.filter(
                            msg => !locallyAdded.find(msg2 => Utils.isEquals(msg, msg2)),
                        );
                    }
                    if (Utils.isNotEmpty(newOtherPresences)) {
                        newState.knownOtherUsers = Utils.copyAndAddAllIfNeeded(
                            state.knownOtherUsers,
                            newOtherPresences,
                        );
                    }
                    if (Utils.isNotEmpty(newOtherPresences) || Utils.isNotEmpty(newMessages)) {
                        newMessages = newMessages.map(nm => Object.assign(nm, { read: false }));
                        this.props.setNewChatMessages(newMessages.length); //so it handles the app global new message indicator
                        newState.chatData = {
                            messages: newMessages
                                ? [...state.chatData?.messages, ...newMessages]
                                : state.chatData?.messages,
                            presences: KeyUtils.updateArray(
                                state.chatData?.presences,
                                fastPolling?.chatData?.presences,
                                CHAT_PRESENCE_KEY_PROVIDER,
                            ),
                        };
                        this.props.onNewChat?.();
                    }
                    this.needToScrollDown = this.needToScrollDown || Utils.isNotEmpty(newMessages);
                    break;
            }
            return newState;
        });
    }
}

export default connector(ChatPanel);
